'use client';

import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { TransformedVideoGalleryData } from '@/types/media-coverage';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import {
  latestVideos,
  shortsItems,
  youtubeShortsUrl,
  youtubeThumbnailUrl,
  type YoutubeThumbnailQuality,
  youtubeWatchUrl,
} from '../video-gallery-data';
import FeaturedVideoGalleryWrap from './FeaturedVideoGalleryWrap';
import CompactPageHero from '@/components/sections/shared/CompactPageHero';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

interface VideoGalleryViewProps {
  locale: string;
  title: string;
  subtitle: string;
  shortsHeading: string;
  latestHeading: string;
  viewMoreLabel: string;
  viewMoreHref: string;
  shortsThumbnailAlt: string;
  latestPlayLabel: string;
  cmsData?: TransformedVideoGalleryData | null;
}

const ITEMS_PER_PAGE = 9;

const SHORT_THUMB_QUALITIES: YoutubeThumbnailQuality[] = ['oar2', 'maxresdefault', 'hqdefault'];
const LATEST_THUMB_QUALITIES: YoutubeThumbnailQuality[] = [
  'maxresdefault',
  'hqdefault',
  'sddefault',
];

function YoutubeVideoThumbnail({
  videoId,
  alt,
  className,
  sizes,
  priority,
  qualities = LATEST_THUMB_QUALITIES,
}: {
  videoId: string;
  alt: string;
  className?: string;
  sizes: string;
  priority?: boolean;
  qualities?: YoutubeThumbnailQuality[];
}) {
  const src = youtubeThumbnailUrl(videoId, qualities[0] ?? 'hqdefault');

  return (
    <CmsImage
      src={src}
      alt={alt}
      fill
      quality={92}
      priority={priority}
      className={className}
      sizes={sizes}
      fallbackSrc={MEDIA_PLACEHOLDER}
    />
  );
}

const DEFAULT_TICKER_LABELS = [
  'YouTube Shorts',
  'Brand Films',
  'Plant Tours',
  'Wonder Cement',
  'Campaigns',
  'Corporate Films',
  'YouTube Shorts',
  'Brand Films',
  'Plant Tours',
  'Wonder Cement',
  'Campaigns',
  'Corporate Films',
];

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [];
  const left = Math.max(2, current - 2);
  const right = Math.min(total - 1, current + 2);
  pages.push(1);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('...');
  pages.push(total);
  return pages;
}

function PlayOverlay() {
  return (
    <span
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
      aria-hidden
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E31E24]/90 shadow-lg transition-transform duration-300 group-hover:scale-110 md:h-12 md:w-12">
        <svg viewBox="0 0 24 24" className="ml-0.5 h-5 w-5 text-white" aria-hidden>
          <path fill="currentColor" d="M8 5v14l11-7z" />
        </svg>
      </span>
    </span>
  );
}

export default function VideoGalleryView({
  locale,
  title,
  subtitle,
  shortsHeading,
  latestHeading,
  viewMoreLabel,
  viewMoreHref,
  shortsThumbnailAlt,
  latestPlayLabel,
  cmsData,
}: VideoGalleryViewProps) {
  const mainRef = useRef<HTMLElement>(null);
  const shortsRef = useRef<HTMLDivElement>(null);
  const latestRef = useRef<HTMLDivElement>(null);
  const [latestPage, setLatestPage] = useState(1);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      gsap.from('.anim-h1', {
        y: 60,
        opacity: 0,
        duration: 1,
        ease: 'power4.out',
        delay: 0.3,
      });

      gsap.from('.anim-subtitle', {
        y: 20,
        opacity: 0,
        duration: 0.75,
        ease: 'power3.out',
        delay: 0.55,
      });

      gsap.from('.anim-section-label', {
        x: -20,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.out',
        immediateRender: false,
        scrollTrigger: {
          trigger: '.anim-section-label',
          start: 'top bottom',
          once: true,
        },
      });

      if (shortsRef.current) {
        gsap.from(shortsRef.current.querySelectorAll('.vg-short-card'), {
          y: 40,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.07,
          immediateRender: false,
          scrollTrigger: {
            trigger: shortsRef.current,
            start: 'top bottom',
            once: true,
          },
        });
      }

      if (latestRef.current) {
        gsap.from(latestRef.current.querySelectorAll('.vg-card'), {
          y: 40,
          opacity: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.07,
          immediateRender: false,
          scrollTrigger: {
            trigger: latestRef.current,
            start: 'top bottom',
            once: true,
          },
        });
      }

      gsap.from('.vg-cta', {
        y: 36,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        immediateRender: false,
        scrollTrigger: {
          trigger: '.vg-cta',
          start: 'top bottom',
          once: true,
        },
      });
    }, mainRef);

    return () => ctx.revert();
  }, []);

  const resolvedTitle = cmsData?.pageTitle || title;
  const subHeadingHtml = cmsData?.subHeadingHtml;
  const resolvedShortsHeading = cmsData?.shorts?.heading || shortsHeading;
  const resolvedLatestHeading = cmsData?.latest?.heading || latestHeading;

  type ShortDisplay = { key: string; id: string };
  type LatestDisplay = { key: string; id: string; caption: string; coverUrl?: string | null };

  const shortsDisplay: ShortDisplay[] =
    cmsData?.shorts.items && cmsData.shorts.items.length > 0
      ? cmsData.shorts.items.map((v) => ({ key: v.key, id: v.id }))
      : shortsItems.map((s) => ({ key: s.id, id: s.id }));

  const latestDisplay: LatestDisplay[] =
    cmsData?.latest.items && cmsData.latest.items.length > 0
      ? cmsData.latest.items.map((v) => ({
          key: v.key,
          id: v.id,
          caption: v.title,
          coverUrl: v.coverUrl,
        }))
      : latestVideos.map((v, idx) => ({ key: `${v.id}-${idx}`, id: v.id, caption: v.caption }));

  const latestTotalPages = Math.ceil(latestDisplay.length / ITEMS_PER_PAGE);
  const latestPageItems = latestDisplay.slice(
    (latestPage - 1) * ITEMS_PER_PAGE,
    latestPage * ITEMS_PER_PAGE,
  );

  return (
    <main ref={mainRef} className="font-figtree text-[#1C1917] antialiased">
      <style>{`
        @keyframes vg-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .vg-ticker-track {
          animation: vg-ticker 30s linear infinite;
          will-change: transform;
        }
        .vg-ticker-track:hover { animation-play-state: paused; }
      `}</style>

      <CompactPageHero>
        <CmsRichTextHeading
          className="anim-h1 hero-title text-white"
          html={cmsData?.titleRichText}
          fallback={resolvedTitle}
        />
        <div className="mt-3 max-w-2xl sm:mt-4">
          {subHeadingHtml ? (
            <div
              className="anim-subtitle hero-subtitle text-white/60 [&_p]:m-0"
              dangerouslySetInnerHTML={{ __html: subHeadingHtml }}
            />
          ) : (
            <p className="anim-subtitle hero-subtitle text-white/60">{subtitle}</p>
          )}
        </div>
      </CompactPageHero>

      <div className="relative z-[1] overflow-hidden bg-[#1C1917] py-3.5 select-none">
        <div className="vg-ticker-track flex items-center whitespace-nowrap">
          {DEFAULT_TICKER_LABELS.map((label, i) => (
            <span key={i} className="inline-flex items-center gap-5 px-5">
              <span className="font-figtree text-[10px] font-bold uppercase tracking-[0.22em] text-white/60">
                {label}
              </span>
              <span className="h-1 w-1 shrink-0 rounded-full bg-[#E31E24]" />
            </span>
          ))}
        </div>
      </div>

      <div className="bg-[#f3f3f3]">
        {shortsDisplay.length > 0 ? (
          <section
            className="container mx-auto max-sm:px-4 px-4 py-10 sm:py-12 md:py-14"
            aria-labelledby="video-gallery-shorts-heading"
            data-section-blur="false"
          >
            <div className="anim-section-label mb-5 flex items-center justify-between sm:mb-6">
              <span
                id="video-gallery-shorts-heading"
                className="font-figtree text-[9px] font-bold uppercase tracking-[0.28em] text-[#A09A93]"
              >
                {resolvedShortsHeading}
              </span>
              <span className="font-figtree text-[9px] font-medium text-[#A09A93]">
                {shortsDisplay.length} videos
              </span>
            </div>

            <div
              ref={shortsRef}
              className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 pt-1 sm:-mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:pb-0 md:grid-cols-4 lg:grid-cols-5"
            >
              {shortsDisplay.map((item, idx) => (
                <a
                  key={item.key}
                  href={youtubeShortsUrl(item.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={shortsThumbnailAlt}
                  className="vg-short-card group relative aspect-[9/16] w-[44vw] max-w-[200px] shrink-0 overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:w-full sm:max-w-none"
                >
                  <YoutubeVideoThumbnail
                    videoId={item.id}
                    alt={shortsThumbnailAlt}
                    priority={idx < 2}
                    qualities={SHORT_THUMB_QUALITIES}
                    className="object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 280px"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
                  <div className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-[#E31E24] transition-transform duration-300 group-hover:scale-x-100" />
                  <span className="absolute bottom-3 left-3 inline-flex items-center rounded-full bg-white px-2.5 py-[5px] font-figtree text-[8px] font-bold uppercase leading-none tracking-[0.14em] text-[#1C1917] shadow-sm">
                    Short
                  </span>
                  <PlayOverlay />
                </a>
              ))}
            </div>

            <div className="mt-6 flex justify-center">
              <a
                href={viewMoreHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-[#e4e4e4] bg-white px-5 py-2.5 font-figtree text-[12px] font-semibold text-[#1C1917] transition-all duration-200 hover:border-[#E31E24] hover:text-[#E31E24] sm:px-6 sm:py-3"
              >
                {viewMoreLabel}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </a>
            </div>
          </section>
        ) : null}

        {latestDisplay.length > 0 ? (
          <section
            className="container mx-auto max-sm:px-4 px-4 py-10 sm:py-12 md:py-14"
            aria-labelledby="video-gallery-latest-heading"
            data-section-blur="false"
          >
            <div className="anim-section-label mb-5 flex items-center justify-between sm:mb-6">
              <span
                id="video-gallery-latest-heading"
                className="font-figtree text-[9px] font-bold uppercase tracking-[0.28em] text-[#A09A93]"
              >
                {resolvedLatestHeading}
              </span>
              <span className="font-figtree text-[9px] font-medium text-[#A09A93]">
                {latestDisplay.length} videos
              </span>
            </div>

            <div
              ref={latestRef}
              className="grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {latestPageItems.map((video) => (
                <article
                  key={video.key}
                  className="vg-card group relative flex flex-col overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <a
                    href={youtubeWatchUrl(video.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${latestPlayLabel}: ${video.caption}`}
                    className="absolute inset-0 z-10"
                  />

                  <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-neutral-100">
                    {video.coverUrl ? (
                      <CmsImage
                        src={video.coverUrl}
                        alt={video.caption}
                        fill
                        quality={92}
                        className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                        fallbackSrc={MEDIA_PLACEHOLDER}
                      />
                    ) : (
                      <YoutubeVideoThumbnail
                        videoId={video.id}
                        alt={video.caption}
                        className="object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
                    <div className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-[#E31E24] transition-transform duration-300 group-hover:scale-x-100" />
                    <span className="absolute bottom-3 left-3 inline-flex items-center rounded-full bg-white px-2.5 py-[5px] font-figtree text-[8px] font-bold uppercase leading-none tracking-[0.14em] text-[#1C1917] shadow-sm">
                      YouTube
                    </span>
                    <PlayOverlay />
                  </div>

                  <div className="flex flex-1 flex-col gap-3 px-5 py-4">
                    <h3 className="flex-1 font-figtree text-[0.9375rem] font-semibold leading-[1.45] text-[#1C1917]">
                      {video.caption}
                    </h3>
                    <div className="flex items-center justify-between gap-2 border-t border-[#e4e4e4] pt-3">
                      <span className="font-figtree text-[10px] tracking-wide text-[#A09A93]">
                        Video
                      </span>
                      <span className="translate-x-2 whitespace-nowrap font-figtree text-[9px] font-bold uppercase tracking-[0.14em] text-[#E31E24] opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
                        Watch →
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {latestTotalPages > 1 ? (
              <>
                <div className="mt-8 flex items-center justify-center gap-1.5">
                  <button
                    onClick={() => setLatestPage((p) => Math.max(1, p - 1))}
                    disabled={latestPage === 1}
                    aria-label="Previous page"
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#e4e4e4] bg-white text-[#1C1917] transition-all duration-200 hover:border-[#E31E24] hover:text-[#E31E24] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-[#e4e4e4] disabled:hover:text-[#1C1917]"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>

                  {getPageNumbers(latestPage, Math.max(1, latestTotalPages)).map((num, i) =>
                    num === '...' ? (
                      <span
                        key={i}
                        className="flex h-9 w-9 items-center justify-center font-figtree text-[12px] text-[#A09A93]"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={num}
                        onClick={() => setLatestPage(num)}
                        aria-label={`Page ${num}`}
                        aria-current={latestPage === num ? 'page' : undefined}
                        className={`flex h-9 w-9 items-center justify-center rounded-xl border font-figtree text-[12px] font-semibold transition-all duration-200 ${
                          latestPage === num
                            ? 'border-[#E31E24] bg-[#E31E24] text-white shadow-sm'
                            : 'border-[#e4e4e4] bg-white text-[#1C1917] hover:border-[#E31E24] hover:text-[#E31E24]'
                        }`}
                      >
                        {num}
                      </button>
                    ),
                  )}

                  <button
                    onClick={() =>
                      setLatestPage((p) => Math.min(Math.max(1, latestTotalPages), p + 1))
                    }
                    disabled={latestPage === Math.max(1, latestTotalPages)}
                    aria-label="Next page"
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#e4e4e4] bg-white text-[#1C1917] transition-all duration-200 hover:border-[#E31E24] hover:text-[#E31E24] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-[#e4e4e4] disabled:hover:text-[#1C1917]"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>

                <p className="mt-3 text-center font-figtree text-[11px] text-[#A09A93]">
                  Page {latestPage} of {Math.max(1, latestTotalPages)}
                </p>
              </>
            ) : (
              <div className="mt-6 flex justify-center">
                <a
                  href={viewMoreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-[#e4e4e4] bg-white px-5 py-2.5 font-figtree text-[12px] font-semibold text-[#1C1917] transition-all duration-200 hover:border-[#E31E24] hover:text-[#E31E24] sm:px-6 sm:py-3"
                >
                  {viewMoreLabel}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </a>
              </div>
            )}
          </section>
        ) : null}

        <FeaturedVideoGalleryWrap locale={locale} viewMoreHref={viewMoreHref} cmsData={cmsData} />
      </div>
    </main>
  );
}
